import React from 'react';

import dayjs from 'dayjs';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import EntityFormField from 'src/features/membership/components/forms/EntityFormField';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { useListMembershipTypes } from 'src/features/membershipType/api/useListMembershipTypes';
import { ConvertActiveStayToMembershipFormSchemaValues } from 'src/features/stay/utils/formSchema';
import { useParking } from 'src/providers/ParkingProvider';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import DatePickerWithTime from 'src/registry/ui/DatePickerWithTime';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Separator } from 'src/registry/ui/Separator';
import { generalMessages } from 'src/registry/utils/messages';
import { translate } from 'src/utils/translate';

type ConvertActiveStayToMembershipFormProps = {
    form: UseFormReturn<ConvertActiveStayToMembershipFormSchemaValues>;
    onSubmit: (values: ConvertActiveStayToMembershipFormSchemaValues) => void;
};
function ConvertActiveStayToMembershipForm({ onSubmit, form }: ConvertActiveStayToMembershipFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: listMembershipTypes } = useListMembershipTypes({ is_active: true, parkingId });

    const validFrom = form.watch('valid_from');
    const selectedMembershipTypeId = form.watch('membership_type');

    const selectedMembershipType = React.useMemo(() => {
        if (selectedMembershipTypeId && listMembershipTypes) {
            return listMembershipTypes.results?.find((type) => type.id === selectedMembershipTypeId);
        }
    }, [selectedMembershipTypeId, listMembershipTypes]);

    const options = React.useMemo(() => {
        if (!listMembershipTypes) return [];
        return listMembershipTypes.results.map(({ translations, id }) => ({
            label: translate(translations, locale)?.name || '',
            value: id,
        }));
    }, [listMembershipTypes, locale]);

    React.useEffect(() => {
        if (selectedMembershipType) {
            if (selectedMembershipType.max_allowed_spaces_count) {
                form.setValue('max_allowed_spaces_count', selectedMembershipType.max_allowed_spaces_count);
            }

            const { duration_unit, duration } = selectedMembershipType;

            if (!duration_unit || !duration || !validFrom) return;

            const newValidTo = dayjs(validFrom).add(duration, duration_unit).toISOString();
            form.setValue('valid_to', newValidTo);
        }
    }, [form, selectedMembershipType, validFrom]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <SelectFormField<ConvertActiveStayToMembershipFormSchemaValues>
                form={form}
                name="membership_type"
                label={formatMessage(membershipMessages.template)}
                options={options}
            />
            <InputFormField<ConvertActiveStayToMembershipFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(generalMessages.name)}
            />
            <Separator />
            <EntityFormField form={form} />
            <Separator />
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(membershipMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime dateISO={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(membershipMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabled={
                                        !!(selectedMembershipType?.duration && selectedMembershipType?.duration_unit)
                                    }
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            {selectedMembershipType?.max_allowed_spaces_count && (
                <InputFormField<ConvertActiveStayToMembershipFormSchemaValues>
                    form={form}
                    name="max_allowed_spaces_count"
                    label={formatMessage(membershipMessages.maxAllowedSpaces)}
                    type="number"
                    disabled
                    required={false}
                    min={0}
                />
            )}
        </Form>
    );
}

export default ConvertActiveStayToMembershipForm;
